import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  ShieldAlert,
  Users,
  Building2,
  Receipt,
  Tag,
  Cpu,
  Database,
  KeyRound,
  UserCog,
  LogOut,
  ChevronDown,
  LayoutGrid,
  Ellipsis,
} from "lucide-react";
import { fetchOk } from "@podecontratar/shared/lib/api";
import { firstName, initials } from "@podecontratar/shared/lib/name";
import { websiteOrigin } from "../lib/website-origin";

const NAV = [
  { href: "/", label: "Visão geral", icon: LayoutDashboard, exact: true },
  { href: "/consultas", label: "Consultas", icon: ShieldCheck },
  { href: "/denuncias", label: "Denúncias", icon: ShieldAlert },
  { href: "/usuarios", label: "Usuários", icon: Users },
  { href: "/empresas", label: "Empresas", icon: Building2 },
  { href: "/pagamentos", label: "Pagamentos", icon: Receipt },
  { href: "/cupons", label: "Cupons", icon: Tag },
  { href: "/backups", label: "Backups", icon: Database },
  { href: "/mcp", label: "MCP", icon: KeyRound },
  { href: "/tecnico", label: "Técnico", icon: Cpu },
];

type Me = {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
};

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const WEBSITE_BASE_URL = websiteOrigin();
  const { pathname } = useLocation();
  const [me, setMe] = useState<Me | null>(null);

  // AuthGate already validated /api/auth/me before mounting this layout
  // (and we keep that the single source of truth for auth). The header
  // chip still needs the name/email/avatar, so we re-read the same
  // response from the same cached endpoint.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (cancelled) return;
        const u = data as { id: string; name?: string; email?: string; avatarUrl?: string | null };
        setMe({
          id: u.id,
          name: (u.name ?? "").trim() || (u.email ?? ""),
          email: u.email ?? "",
          avatarUrl: u.avatarUrl ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex min-h-screen bg-surface-alt/40">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border bg-surface md:flex">
        {/* Top: brand + user. Stays put when the nav scrolls. */}
        <div className="shrink-0 px-3 pt-6">
          <a
            href={`${WEBSITE_BASE_URL}/painel`}
            className="mb-4 flex items-center gap-2 px-3"
          >
            <img
              src="/wordmark-light.svg"
              alt="pode contratar"
              height={20}
              style={{ height: 20, width: "auto" }}
            />
            <span className="rounded-full bg-text px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-text-inverse">
              Admin
            </span>
          </a>
          {me && <UserButton me={me} />}
        </div>
        {/* Middle: nav scrolls internally if the list grows past the
            viewport. flex-1 + overflow-y-auto keeps the brand/user
            pinned at the top and "Voltar ao painel" pinned at the
            bottom regardless of nav height. */}
        <nav aria-label="Admin" className="mt-5 flex-1 overflow-y-auto px-3">
          <ul className="flex flex-col gap-1 pb-2">
            {NAV.map((item) => {
              const active = item.exact
                ? pathname === item.href
                : pathname.startsWith(item.href);
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm tracking-tight transition-colors ${
                      active
                        ? "bg-accent-soft font-medium text-accent"
                        : "text-text-secondary hover:bg-surface-alt hover:text-text"
                    }`}
                  >
                    <item.icon
                      size={15}
                      strokeWidth={1.75}
                      className={active ? "text-accent" : "text-text-tertiary"}
                      aria-hidden
                    />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        {/* Bottom: pinned. */}
        <div className="shrink-0 border-t border-border px-3 py-3">
          <a
            href={`${WEBSITE_BASE_URL}/painel`}
            className="block rounded-lg px-3 py-2 text-xs text-text-tertiary transition-colors hover:bg-surface-alt hover:text-text"
          >
            ← Voltar ao painel
          </a>
        </div>
      </aside>

      {/* Mobile nav: 4 top-priority items + drawer "Mais" for the rest.
          Fitting all 10 NAV entries inline at phone widths squeezes
          each item to ~30px which is below any reasonable touch
          target. */}
      <MobileNav pathname={pathname} />

      <main className="flex-1 overflow-x-hidden pb-20 md:pb-0">{children}</main>
    </div>
  );
}

const MOBILE_PRIMARY: typeof NAV = NAV.filter((n) =>
  ["/", "/consultas", "/usuarios", "/denuncias"].includes(n.href)
);

function MobileNav({ pathname }: { pathname: string }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const moreItems = NAV.filter((n) => !MOBILE_PRIMARY.includes(n));
  const moreActive = moreItems.some((n) =>
    n.exact ? pathname === n.href : pathname.startsWith(n.href)
  );

  return (
    <>
      <nav
        aria-label="Admin mobile"
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-surface md:hidden"
      >
        <ul className="flex items-stretch">
          {MOBILE_PRIMARY.map((item) => {
            const active = item.exact
              ? pathname === item.href
              : pathname.startsWith(item.href);
            return (
              <li key={item.href} className="flex-1">
                <Link
                  to={item.href}
                  className={`flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium tracking-tight transition-colors ${
                    active ? "text-accent" : "text-text-tertiary"
                  }`}
                >
                  <item.icon size={18} strokeWidth={1.75} aria-hidden />
                  {item.label}
                </Link>
              </li>
            );
          })}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className={`flex w-full flex-col items-center gap-0.5 py-2 text-[10px] font-medium tracking-tight transition-colors ${
                moreActive ? "text-accent" : "text-text-tertiary"
              }`}
            >
              <Ellipsis size={18} strokeWidth={1.75} aria-hidden />
              Mais
            </button>
          </li>
        </ul>
      </nav>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setDrawerOpen(false)}
        >
          <div className="absolute inset-0 bg-text/40" aria-hidden />
          <div
            role="dialog"
            aria-label="Menu admin"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-0 left-0 right-0 rounded-t-2xl border-t border-border bg-surface p-4 pb-6"
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border" aria-hidden />
            <ul className="grid grid-cols-3 gap-2">
              {moreItems.map((item) => {
                const active = item.exact
                  ? pathname === item.href
                  : pathname.startsWith(item.href);
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      onClick={() => setDrawerOpen(false)}
                      className={`flex flex-col items-center gap-1 rounded-lg border border-border px-2 py-3 text-[11px] font-medium transition-colors ${
                        active
                          ? "border-accent/50 bg-accent-soft text-accent"
                          : "bg-surface text-text hover:bg-surface-alt"
                      }`}
                    >
                      <item.icon size={18} strokeWidth={1.75} aria-hidden />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

function UserButton({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const WEBSITE_BASE_URL = websiteOrigin();

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current || ref.current.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onEsc);
    }
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  function handleLogout() {
    // Bounce to the website's /auth/logout page so it can run its full
    // cleanup (POST + pc.* localStorage sweep on the website origin
    // where those caches live). The shared cookie on
    // Domain=.podecontratar.com.br means our session here dies the
    // moment the API sets Max-Age=0.
    setOpen(false);
    window.location.href = `${websiteOrigin()}/auth/logout`;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-lg border border-border bg-surface px-2 py-1.5 transition-colors hover:border-text/20 hover:bg-surface-alt"
      >
        <span
          className="grid h-6 w-6 shrink-0 place-items-center overflow-hidden rounded-full bg-accent text-[10px] font-semibold text-text-inverse"
          aria-hidden
        >
          {me.avatarUrl ? (
            <img src={me.avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            initials(me.name)
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-left text-xs font-medium tracking-tight text-text">
          {firstName(me.name)}
        </span>
        <ChevronDown
          size={12}
          className={`shrink-0 text-text-tertiary transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 right-0 top-[calc(100%+8px)] overflow-hidden rounded-2xl border border-border bg-surface shadow-[var(--shadow-card-lift)]"
        >
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            <span
              className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-accent text-xs font-semibold text-text-inverse"
              aria-hidden
            >
              {me.avatarUrl ? (
                <img src={me.avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                initials(me.name)
              )}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold tracking-tight text-text">
                {me.name}
              </p>
              {me.email && (
                <p className="mt-0.5 truncate text-xs text-text-tertiary">{me.email}</p>
              )}
            </div>
          </div>
          <ul className="p-1.5">
            <li>
              <a
                href={`${WEBSITE_BASE_URL}/painel`}
                onClick={() => setOpen(false)}
                role="menuitem"
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-text transition-colors hover:bg-surface-alt"
              >
                <LayoutGrid size={15} strokeWidth={1.75} className="text-text-tertiary" aria-hidden />
                Voltar ao painel
              </a>
            </li>
            <li>
              <a
                href={`${WEBSITE_BASE_URL}/painel/conta`}
                onClick={() => setOpen(false)}
                role="menuitem"
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-text transition-colors hover:bg-surface-alt"
              >
                <UserCog size={15} strokeWidth={1.75} className="text-text-tertiary" aria-hidden />
                Editar conta
              </a>
            </li>
          </ul>
          <div className="border-t border-border p-1.5">
            <button
              type="button"
              onClick={handleLogout}
              role="menuitem"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-text transition-colors hover:bg-surface-alt"
            >
              <LogOut size={15} strokeWidth={1.75} className="text-text-tertiary" aria-hidden />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
